/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SortableNavRow } from '@layout/agency/SortableNavRow';
import { Text, UnstyledButton } from '@/shared/ui';
import {
  DESCRIPTION_FIELD_KEYS,
  DEFAULT_DESCRIPTION_FIELDS,
  type DescriptionFieldKey,
} from './description-template-fields';

export interface PostDescriptionTemplateModalProps {
  open: boolean;
  onClose: () => void;
  /** Current agency selection, in order; null/empty means "use the default order". */
  fields: DescriptionFieldKey[] | null | undefined;
  onSave: (fields: DescriptionFieldKey[]) => void;
  t: (key: string) => string;
}

/** Matches the sidebar nav's own drag activation: a long-press (not an instant click) starts the reorder. */
const DRAG_ACTIVATION = { delay: 120, tolerance: 8 };

/**
 * Lets an agency pick which property fields go into the auto-generated
 * Facebook/Instagram post description, and in what order. Saved as
 * `post_description_fields` and rendered server-side by both networks.
 *
 * @param props - See {@link PostDescriptionTemplateModalProps}.
 * @returns The modal element, or null when closed.
 */
export function PostDescriptionTemplateModal({ open, onClose, fields, onSave, t }: PostDescriptionTemplateModalProps) {
  const [selected, setSelected] = useState<DescriptionFieldKey[]>(
    () => (fields && fields.length > 0 ? fields : DEFAULT_DESCRIPTION_FIELDS),
  );
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: DRAG_ACTIVATION }));

  if (!open) return null;

  const available = DESCRIPTION_FIELD_KEYS.filter((key) => !selected.includes(key));

  const addField = (key: DescriptionFieldKey) => setSelected((prev) => [...prev, key]);
  const removeField = (key: DescriptionFieldKey) => setSelected((prev) => prev.filter((k) => k !== key));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSelected((prev) => {
      const oldIndex = prev.indexOf(active.id as DescriptionFieldKey);
      const newIndex = prev.indexOf(over.id as DescriptionFieldKey);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/40" />
      <div
        className="relative w-full max-w-lg mx-(--space-4) rounded-(--radius-xl) bg-(--surface-primary) border border-(--border-subtle) shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-(--space-6) py-(--space-4) border-b border-(--border-subtle)">
          <Text className="m-0 text-base font-semibold text-(--text-primary)">
            {t('facebook.postDescription.modalTitle')}
          </Text>
          <UnstyledButton
            onClick={onClose}
            data-testid="post-description-modal-close"
            className="w-7 h-7 flex items-center justify-center rounded-(--radius-md) border-none bg-transparent hover:bg-(--surface-secondary) cursor-pointer text-(--text-tertiary) text-lg"
          >
            &times;
          </UnstyledButton>
        </div>

        <div className="px-(--space-6) py-(--space-5) space-y-(--space-4) max-h-[70vh] overflow-y-auto">
          <Text className="m-0 text-xs text-(--text-tertiary)">
            {t('facebook.postDescription.modalDescription')}
          </Text>

          <div>
            <Text className="m-0 text-xs font-semibold text-(--text-primary) mb-(--space-2)">
              {t('facebook.postDescription.includedLabel')}
            </Text>
            {selected.length === 0 && (
              <Text className="m-0 text-xs text-(--text-tertiary) italic">
                {t('facebook.postDescription.availableLabel')}
              </Text>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selected} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-(--space-2)">
                  {selected.map((key) => (
                    <SortableNavRow key={key} id={key} showHandle>
                      <div
                        data-testid={`post-description-selected-${key}`}
                        className="flex items-center gap-(--space-2) px-(--space-3) py-(--space-2) rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-secondary)"
                      >
                        <Text component="span" className="flex-1 text-sm text-(--text-primary)">
                          {t(`facebook.postDescription.fields.${key}`)}
                        </Text>
                        <UnstyledButton
                          onClick={() => removeField(key)}
                          data-testid={`post-description-remove-${key}`}
                          aria-label={t('facebook.postDescription.remove')}
                          className="w-6 h-6 flex items-center justify-center rounded-(--radius-sm) border-none bg-transparent cursor-pointer text-(--text-tertiary) hover:text-[var(--status-error)]"
                        >
                          &times;
                        </UnstyledButton>
                      </div>
                    </SortableNavRow>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          {available.length > 0 && (
            <div className="pt-(--space-3) border-t border-(--border-subtle)">
              <Text className="m-0 text-xs font-semibold text-(--text-primary) mb-(--space-2)">
                {t('facebook.postDescription.availableLabel')}
              </Text>
              <div className="flex flex-wrap gap-(--space-2)">
                {available.map((key) => (
                  <UnstyledButton
                    key={key}
                    onClick={() => addField(key)}
                    data-testid={`post-description-add-${key}`}
                    className="px-(--space-3) py-(--space-1-5) text-xs rounded-(--radius-md) border border-dashed border-(--border-subtle) bg-transparent cursor-pointer text-(--text-secondary) hover:border-(--seed-base) hover:text-(--seed-base)"
                  >
                    + {t(`facebook.postDescription.fields.${key}`)}
                  </UnstyledButton>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-(--space-6) py-(--space-3) border-t border-(--border-subtle) flex justify-end gap-(--space-2)">
          <UnstyledButton
            onClick={onClose}
            data-testid="post-description-cancel-btn"
            className="px-(--space-4) py-(--space-2) text-xs rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-secondary) hover:bg-(--surface-primary) cursor-pointer"
          >
            {t('facebook.postDescription.cancel')}
          </UnstyledButton>
          <UnstyledButton
            onClick={handleSave}
            data-testid="post-description-save-btn"
            className="px-(--space-4) py-(--space-2) text-xs font-semibold rounded-(--radius-md) border-none cursor-pointer hover:opacity-90"
            style={{ background: 'var(--seed-base)', color: 'var(--surface-primary)' }}
          >
            {t('facebook.postDescription.save')}
          </UnstyledButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
